import React, {useRef} from 'react';
import PageTitle from '../../../../uiToolkit/PageTitle';
import {microlessonsListText} from '../microlessonsListText';
import SearchInput from '../../../../uiToolkit/Inputs/SearchInput';
import Button from '../../../../uiToolkit/Buttons/Button';
import './styles.css';
import MicrolessonListCard from '../components/MicrolessonListCard';
import {Col, Container, Row} from 'react-bootstrap';
import {MicrolessonListItem} from '../../../../data/models/Microlessons/MicrolessonListItem';
import MicrolessonsEmptyState from '../../../../uiToolkit/EmptyStates/MicrolessonsEmptyState';
import {Tour} from '../../../../modules/Tour';

interface Props {
  lessons: MicrolessonListItem[];
  onEditLesson: (id: string) => void;
  onPreviewLesson: (id: string) => void;
  onCreateLesson: () => void;
  onEditLessonInfo: (lessonId: string) => void;
  onEditLessonVideo: (id: string) => void;
  onDelete: (id: string) => void;
  isLoading: boolean;
  loadNextPage: () => Promise<any>;
  search: string;
  setSearch: (search: string) => void;
}

const MicrolessonsListUI = React.memo(
  ({
    lessons,
    onEditLesson,
    onPreviewLesson,
    onCreateLesson,
    onEditLessonInfo,
    onEditLessonVideo,
    onDelete,
    isLoading,
    loadNextPage,
    search,
    setSearch,
  }: Props) => {
    const observer = useRef<any>();

    const overscrollLock = useRef(false);

    const lastPostElementRef = (node: any) => {
      if (isLoading || overscrollLock.current) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          overscrollLock.current = true;
          loadNextPage().then(() => {
            overscrollLock.current = false;
          });
        }
      });

      if (node) observer.current.observe(node);
    };

    return (
      <div className="LessonsListContainer">
        <Tour section="microlessonsList" />
        <div className="LessonsListHeader">
          <PageTitle title={microlessonsListText.title} />
          <div className="LessonsFiltersContainer">
            <SearchInput
              value={search}
              onChange={setSearch}
              className="Input"
            />
            <Button
              title={microlessonsListText.create}
              onClick={onCreateLesson}
              uiType="action"
              icon="icon-plus"
              className="createLessonButton"
            />
          </div>
        </div>
        <div>
          <Container fluid className="LessonsList">
            <Row>
              {lessons.length ? (
                lessons?.map((lesson, index) => (
                  <Col
                    xl={3}
                    lg={4}
                    key={lesson.id}
                    className="LessonsCol"
                    ref={
                      lessons.length === index + 1 ? lastPostElementRef : null
                    }>
                    <MicrolessonListCard
                      lesson={lesson}
                      onEditLesson={onEditLesson}
                      onPreviewLesson={onPreviewLesson}
                      onEditLessonInfo={onEditLessonInfo}
                      onEditLessonVideo={onEditLessonVideo}
                      onDelete={onDelete}
                    />
                  </Col>
                ))
              ) : (
                <Col xl={12} lg={12}>
                  <MicrolessonsEmptyState
                    onCreate={onCreateLesson}
                    canCreateCourses
                  />
                </Col>
              )}
            </Row>
          </Container>
        </div>
      </div>
    );
  },
);

export default MicrolessonsListUI;
